import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import SlideOver from "@/components/SlideOver";
import UpgradeModal from "@/components/UpgradeModal";
import { useAuth } from "@/context/AuthContext";
import { isPremiumUnlocked } from "@/lib/upgrade";
import {
  Users, Plus, Crown, Vote, Copy, Check, Loader2, Truck, Send, Gift, Bell, Sparkles,
} from "lucide-react";

const Circles = () => {
  const { user } = useAuth();
  const premiumUnlocked = isPremiumUnlocked(user);
  const [circles, setCircles] = useState([]);
  const [lovedOnes, setLovedOnes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [nf, setNf] = useState({ loved_one_id: "", occasion: "Celebration", members: "" });
  const [creating, setCreating] = useState(false);
  const [active, setActive] = useState(null); // active circle detail
  const [busy, setBusy] = useState(false);
  const [cash, setCash] = useState("");
  const [copied, setCopied] = useState(false);
  const [upgradeGate, setUpgradeGate] = useState(false);

  const load = useCallback(async () => {
    const [c, lo] = await Promise.all([api.get("/circles"), api.get("/loved-ones")]);
    setCircles(c.data);
    setLovedOnes(lo.data);
    if (!nf.loved_one_id && lo.data.length) setNf((s) => ({ ...s, loved_one_id: lo.data[0].id }));
    setLoading(false);
  }, [nf.loved_one_id]);
  useEffect(() => { load(); }, [load]);

  const openCircle = async (id) => {
    const r = await api.get(`/circles/${id}`);
    setActive(r.data);
    setCash(r.data.cash_gift?.amount || "");
    // poll if still generating
    if (r.data.status === "generating") {
      const t = setInterval(async () => {
        const rr = await api.get(`/circles/${id}`);
        setActive(rr.data);
        if (rr.data.status !== "generating") clearInterval(t);
      }, 5000);
    }
  };

  const createCircle = async () => {
    if (!nf.loved_one_id) return;
    setCreating(true);
    try {
      const members = nf.members.split(",").map((m) => m.trim()).filter(Boolean).map((name) => ({ name }));
      const r = await api.post("/circles", { loved_one_id: nf.loved_one_id, occasion: nf.occasion, members });
      setCreateOpen(false);
      await load();
      openCircle(r.data.id);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.error === "upgrade_required") setUpgradeGate(true);
      else alert(d || "Could not start the Circle.");
    } finally { setCreating(false); }
  };

  const loungeUrl = active ? `${window.location.origin}/circle/${active.lounge_token}` : "";
  const copyLounge = () => { navigator.clipboard?.writeText(loungeUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  const saveCash = async () => {
    if (!active) return;
    await api.post(`/circles/${active.id}/cash-gift`, { amount: parseFloat(cash) || 0 });
    openCircle(active.id);
  };

  const observe = async () => {
    setBusy(true);
    try { const r = await api.post(`/circles/${active.id}/observe`); setActive({ ...r.data, lounge_token: active.lounge_token, is_owner: true }); }
    finally { setBusy(false); }
  };

  const nudge = async () => {
    const r = await api.post(`/circles/${active.id}/nudge`);
    alert(`Nudge sent to ${r.data.nudged} co-signer(s). ${r.data.note}`);
  };

  const mint = async (method) => {
    if (!active) return;
    if (!window.confirm(`Boss Veto: finalize & ${method === "physical" ? "mail" : "deliver"} this card for the whole Circle?`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/circles/${active.id}/mint`, { card_id: active.leading_card_id, delivery_method: method });
      if (navigator.vibrate) navigator.vibrate([40, 60, 40]);
      alert(`Minted! Royal Toast link: ${r.data.toast_url}`);
      openCircle(active.id);
    } catch (e) {
      alert(e?.response?.data?.detail || "Mint failed.");
    } finally { setBusy(false); }
  };

  return (
    <AppShell>
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-4xl mb-1 flex items-center gap-3">The Gifted Circle <Users className="w-7 h-7 text-primary" /></h1>
          <p className="text-muted-foreground">Curate a card together — vote, sign, and let the Owner mint &amp; mail.</p>
        </div>
        <button data-testid="create-circle-btn" onClick={() => (premiumUnlocked ? setCreateOpen(true) : setUpgradeGate(true))}
          className="flex items-center gap-2 bg-gradient-to-r from-primary to-secondary text-white rounded-2xl px-5 py-2.5 hover:opacity-90">
          {premiumUnlocked ? <><Plus className="w-4 h-4" /> Start a Circle</> : <><Crown className="w-4 h-4" /> Unlock Premium</>}
        </button>
      </div>

      {!premiumUnlocked ? (
        <div className="surface rounded-[2rem] p-16 text-center" data-testid="premium-lock">
          <Crown className="w-12 h-12 mx-auto mb-4 gold-text" />
          <h3 className="font-serif text-3xl mb-2">A premium privilege</h3>
          <p className="text-muted-foreground mb-6">The Gifted Circle — collaborative curation, the Lounge, Digital Ink co-signing & the Champagne Toast reveal — unlocks permanently with any Credit Pack.</p>
          <button onClick={() => setUpgradeGate(true)} data-testid="premium-lock-cta" className="inline-flex items-center gap-2 bg-gradient-to-r from-primary to-secondary text-white px-6 py-3 rounded-2xl hover:opacity-90"><Crown className="w-4 h-4" /> Buy a Credit Pack</button>
        </div>
      ) : loading ? (
        <div className="text-center py-24 text-muted-foreground">Loading circles…</div>
      ) : circles.length === 0 ? (
        <div className="surface rounded-[2rem] p-16 text-center">
          <Users className="w-12 h-12 mx-auto mb-4 text-primary/60" />
          <h3 className="font-serif text-2xl mb-2">No circles yet</h3>
          <p className="text-muted-foreground mb-6">Start a collaborative curation and invite friends to the Royal Lounge.</p>
          <button onClick={() => setCreateOpen(true)} className="inline-flex items-center gap-2 bg-gradient-to-r from-primary to-secondary text-white px-6 py-3 rounded-2xl hover:opacity-90"><Plus className="w-4 h-4" /> Start a Circle</button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 stagger" data-testid="circles-grid">
          {circles.map((c) => (
            <button key={c.id} onClick={() => openCircle(c.id)} className="surface rounded-[1.5rem] p-6 text-left card-soft" data-testid="circle-card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-wider gold-text">{c.occasion}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border ${c.status === "minted" ? "bg-secondary/10 text-secondary border-secondary/30" : c.status === "generating" ? "bg-yellow-500/10 gold-text border-yellow-500/20" : "bg-primary/10 text-primary border-primary/20"}`}>{c.status}</span>
              </div>
              <div className="font-serif text-xl mb-1">{c.loved_one_name}</div>
              <div className="text-sm text-muted-foreground">{(c.signatures || []).length} signature(s) · {Object.values(c.votes || {}).reduce((a, v) => a + v.length, 0)} vote(s)</div>
            </button>
          ))}
        </div>
      )}

      {/* Create Circle drawer */}
      <SlideOver open={createOpen} onClose={() => setCreateOpen(false)} title="Start a Circle" subtitle="We'll curate 3 designs for the group to vote on">
        <div className="space-y-4">
          <div>
            <label className="text-sm text-muted-foreground">Loved one</label>
            <select data-testid="circle-loved-one" value={nf.loved_one_id} onChange={(e) => setNf({ ...nf, loved_one_id: e.target.value })}
              className="w-full mt-1 bg-input border border-border rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-ring/40">
              {lovedOnes.map((lo) => <option key={lo.id} value={lo.id}>{lo.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Occasion</label>
            <input data-testid="circle-occasion" value={nf.occasion} onChange={(e) => setNf({ ...nf, occasion: e.target.value })}
              className="w-full mt-1 bg-input border border-border rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-ring/40" />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Invite members (comma-separated names)</label>
            <input data-testid="circle-members" value={nf.members} onChange={(e) => setNf({ ...nf, members: e.target.value })}
              className="w-full mt-1 bg-input border border-border rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-ring/40" placeholder="Alex, Sam, Jordan" />
          </div>
          <button data-testid="circle-create-submit" onClick={createCircle} disabled={creating}
            className="w-full bg-gradient-to-r from-primary to-secondary text-white rounded-2xl py-3.5 hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2">
            {creating ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating…</> : <><Sparkles className="w-4 h-4" /> Create & Curate 3 Designs</>}
          </button>
        </div>
      </SlideOver>

      {/* Manage Circle drawer (owner) */}
      <SlideOver open={!!active} onClose={() => setActive(null)} title="Royal Lounge" subtitle={active ? `${active.loved_one_name} · ${active.occasion}` : ""} width="max-w-xl">
        {active && (
          <div className="space-y-6">
            {/* Share link */}
            <div className="surface rounded-2xl p-3 flex items-center gap-2" data-testid="lounge-share">
              <input readOnly value={loungeUrl} className="flex-1 bg-transparent text-xs text-muted-foreground truncate outline-none" />
              <button onClick={copyLounge} className="text-xs flex items-center gap-1 text-primary">{copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Invite</>}</button>
            </div>

            {active.status === "generating" ? (
              <div className="text-center py-8 text-muted-foreground flex items-center justify-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Curating 3 designs…</div>
            ) : (
              <>
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium flex items-center gap-2"><Vote className="w-4 h-4 text-primary" /> Designs &amp; votes</h3>
                    <button data-testid="observe-btn" onClick={observe} disabled={busy} className="text-xs surface rounded-lg px-3 py-1.5 text-primary hover:border-primary/40">
                      {busy ? "Observing…" : "Vision: refine leading"}
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-3" data-testid="circle-candidates">
                    {active.candidates.map((c) => (
                      <div key={c.id} className={`rounded-xl overflow-hidden border ${active.leading_card_id === c.id ? "ring-2 ring-yellow-500/50 border-transparent" : "border-border"}`}>
                        <img src={`data:image/png;base64,${c.image_base64}`} alt="" className="w-full h-24 object-cover" />
                        <div className="text-center text-xs py-1">{(active.votes?.[c.id] || []).length} ♥</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="text-sm text-muted-foreground" data-testid="circle-signatures">
                  Signed by: {active.signatures.length ? active.signatures.map((s) => s.name).join(", ") : "no one yet"}
                </div>

                {/* Cash gift */}
                <div className="surface rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-2"><Gift className="w-4 h-4 gold-text" /><span className="text-sm font-medium">Add a cash gift</span></div>
                  <div className="flex gap-2">
                    <input data-testid="circle-cash" type="number" value={cash} onChange={(e) => setCash(e.target.value)}
                      className="flex-1 bg-input border border-border rounded-2xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-ring/40" placeholder="Amount $" />
                    <button data-testid="circle-cash-save" onClick={saveCash} className="surface rounded-2xl px-4 text-primary hover:border-primary/40">Set</button>
                  </div>
                  {active.cash_gift && <p className="text-xs text-muted-foreground mt-2">Cash gift: ${active.cash_gift.amount} · held in the Group Pot</p>}
                </div>

                <button data-testid="circle-nudge" onClick={nudge} className="w-full flex items-center justify-center gap-2 surface rounded-2xl py-3 text-muted-foreground hover:text-foreground">
                  <Bell className="w-4 h-4" /> Nudge co-signers
                </button>

                {/* Boss Veto */}
                {active.status !== "minted" ? (
                  <div className="pt-2 border-t border-border/50">
                    <div className="flex items-center gap-2 mb-2 text-sm"><Crown className="w-4 h-4 gold-text" /> Boss Veto — only you can finalize</div>
                    <div className="flex gap-3">
                      <button data-testid="mint-digital" onClick={() => mint("digital")} disabled={busy}
                        className="flex-1 flex items-center justify-center gap-2 surface rounded-2xl py-3.5 text-secondary hover:border-secondary/40"><Send className="w-4 h-4" /> Mint · Digital</button>
                      <button data-testid="mint-mail" onClick={() => mint("physical")} disabled={busy}
                        className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-secondary to-primary text-white rounded-2xl py-3.5 hover:opacity-90"><Truck className="w-4 h-4" /> Mint &amp; Mail</button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-secondary flex items-center justify-center gap-2 pt-2"><Check className="w-5 h-5" /> Minted &amp; on its way</div>
                )}
              </>
            )}
          </div>
        )}
      </SlideOver>
      <UpgradeModal open={upgradeGate} onClose={() => setUpgradeGate(false)} tier="credit_pack" feature="The Gifted Circle (Multi-Signer)" />
    </AppShell>
  );
};

export default Circles;
